import React, { useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import Select from 'react-select';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  createPartsCriticality,
  deletePartsCriticality,
  getPartsCriticality,
  updatePartsCriticality,
} from '../api/parts-criticalityAPI';
import { fetchData } from '../utils/apiHelpers';
import { getAll } from '../api';

const PartsCriticalityLayer = () => {
  const userProfile = useUserProfile();
  const [criticalities, setCriticalities] = useState([]);
  const [colours, setColours] = useState([]);
  const [newCriticality, setNewCriticality] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const colourOptions = colours.map((c) => ({
    value: c.id,
    label: c.name,
    bootstrapName: c.bootstrapName,
  }));

  const fetchCriticalities = () => {
    fetchData(getPartsCriticality, {}, setCriticalities);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newCriticality.name?.trim()) newErrors.name = 'Criticality name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchCriticalities();
    fetchData(getAll, { apiName: 'colours' }, setColours);
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (criticalities.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [criticalities]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleEdit = (item) => {
    setNewCriticality(item);
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this part criticality?')) return;
    try {
      await deletePartsCriticality(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchCriticalities();
    } catch (error) {
      console.error('Error deleting criticality:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const payload = {
        name: newCriticality.name,
        description: newCriticality.description || null,
        coloursId: newCriticality.coloursId || null,
      };

      if (editingId) {
        await updatePartsCriticality(editingId, payload);
      } else {
        await createPartsCriticality(payload);
      }

      setShowModal(false);
      setEditingId(null);

      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      await fetchCriticalities();
      setNewCriticality({});
    } catch (error) {
      console.error('Error saving criticality:', error);
    }
  };

  const renderColour = (item) => {
    const colour = item.colours;
    if (!colour) return '';
    return (
      <span>
        <span
          className={`d-inline-block rounded-1 bg-${colour.bootstrapName}`}
          style={{ width: '10px', height: '10px', marginRight: '8px' }}
        />
        {colour.name}
      </span>
    );
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('parts-criticality:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            onClick={() => {
              setNewCriticality({});
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Part Criticality
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Colour</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {criticalities.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.name}</strong></td>
                  <td>{item.description || '-'}</td>
                  <td>{renderColour(item)}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('parts-criticality:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('parts-criticality:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Part Criticality' : 'New Part Criticality'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={newCriticality.name || ''}
                      onChange={(e) => setNewCriticality((prev) => ({ ...prev, name: e.target.value }))}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Colour</label>
                    <Select
                      options={colourOptions}
                      value={colourOptions.find((option) => option.value === newCriticality.coloursId) || null}
                      onChange={(option) => setNewCriticality((prev) => ({ ...prev, coloursId: option?.value }))}
                      isClearable
                    />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={newCriticality.description || ''}
                      onChange={(e) => setNewCriticality((prev) => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartsCriticalityLayer;
